import { Component, Input, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { SharedService } from 'src/app/core/services/shared.service';
import Swal from 'sweetalert2';

// almora 2026.06.11 Componente generico de mapeo/asociacion entre dos listas (SYS02TIP = 'MAP').
// Reutiliza el endpoint ListarGenerico (una llamada por lista) para llenar cada grid, y un toolbar
// que viene definido desde el backend (JSON MAP). Al presionar un boton se envian las llaves de la
// seleccion 1 (LLAVES) y la seleccion 2 (FILTROS_OBLIGATORIOS) al WS indicado por el boton (SYS16URL).

interface ColumnaMapeo {
  A03COD: string;
  A03DES: string;
  SYS03TIP?: string;   // 'F' | 'N' => alineado a la derecha
}

interface FilaMapeo {
  INDEX: number;
  checkado: boolean;
  [campo: string]: any;
}

interface ListaMapeo {
  sys02con: string;
  titulo: string;
  estructura: any[];          // Estructura completa del advancetable
  columnas: ColumnaMapeo[];   // columnas visibles (A03VIS = 'S')
  llaves: string[];           // campos PK (A03PKY = 'S')
  filas: FilaMapeo[];         // DataTable de la pagina actual
  filtros: any[];             // Filtros crudos (resp.Filtros) de la lista1; alimentan el sidebar
  operaciones: any[];         // botonera de operaciones por fila (resp.Operaciones, SYS16MET='S')
  pagina: number;
  offset: number;
  hayMas: boolean;            // la pagina actual trajo offset registros => puede haber siguiente
  cargando: boolean;
  todos: boolean;             // estado del checkbox "marcar todos"
}

// Campo de filtro que pinta el sidebar propio (derivado de resp.Filtros de la lista1).
interface CampoFiltro {
  label: string;
  title: string;
  type: string;
  placeholder?: string;
  options?: any[];
  avanzado: boolean;
}

@Component({
  selector: 'app-mapeo-registros',
  templateUrl: './mapeo-registros.component.html',
  styleUrls: ['./mapeo-registros.component.scss']
})
export class MapeoRegistrosComponent implements OnInit {

  @Input() strWsURL: string = '';        // URL del loader MAP: api/mantenimientos/ListarMapeo/{SYS02CON}
  @Input() strSYS02TIP: string = '';
  // almora 2026.07.23 Modo embebido en wizard: llaves del registro padre (item.Llaves).
  // Se inyectan como FILTROS_OBLIGATORIOS al listar cada lista (igual que WsLlaves en advancedtable)
  // y viajan al WS de asociacion como LLAVES_PADRE. En modo top-level llega vacio => arreglo [].
  @Input() llaves: any = '';

  private urlListar = 'api/mantenimientos/ListarGenerico/';

  // Llaves del padre normalizadas a arreglo de objetos (formato FILTROS_OBLIGATORIOS).
  private llavesPadre: any[] = [];

  titulo = 'Mapeo de Registros';
  toolbar: any[] = [];
  cargandoMapa = true;

  // Un solo filtro (sidebar delgado a la izquierda) que aplica a AMBAS listas.
  // Se pinta con markup propio (NO app-filtrocampo), derivado de resp.Filtros de la lista1.
  filtrosAplicados: any = {};
  camposFiltro: CampoFiltro[] = [];     // campos a pintar en el sidebar (todos, siempre abiertos)
  filtroModel: any = {};                // valores actuales del sidebar (label -> valor)

  lista1: ListaMapeo = this.listaVacia();
  lista2: ListaMapeo = this.listaVacia();

  constructor(private dataService: SharedService) { }

  ngOnInit(): void {
    this.llavesPadre = this.normalizarLlaves(this.llaves);
    this.cargarMapa();
  }

  // Normaliza item.Llaves (string JSON u objeto/arreglo) a un arreglo de objetos de llaves.
  // Espejo de como advancedtable trata WsLlaves y ausencias trata su input 'llaves'.
  private normalizarLlaves(input: any): any[] {
    if (input == null || input === '') return [];
    let obj: any = input;
    if (typeof input === 'string') {
      try { obj = JSON.parse(input); } catch { return []; }
    }
    if (Array.isArray(obj)) return obj;
    if (Array.isArray(obj?.LLAVES)) return obj.LLAVES;
    if (Array.isArray(obj?.FILTROS_OBLIGATORIOS)) return obj.FILTROS_OBLIGATORIOS;
    return [obj];
  }

  private listaVacia(): ListaMapeo {
    return {
      sys02con: '', titulo: '', estructura: [], columnas: [], llaves: [],
      filas: [], filtros: [], operaciones: [], pagina: 1, offset: 10, hayMas: false,
      cargando: false, todos: false
    };
  }

  // 1) Carga el JSON MAP { SYS02CON_PRIMARIO, SYS02CON_SECUNDARIO, Toolbar }
  cargarMapa(): void {
    this.cargandoMapa = true;
    this.dataService.ConsultarWS(this.strWsURL).subscribe(
      (resp: any) => {
        const r = resp || {};
        // tolerante a la variante de nombre SYS20CON_SECUNTARIO
        this.lista1.sys02con = r.SYS02CON_PRIMARIO || '';
        this.lista2.sys02con = r.SYS02CON_SECUNDARIO || r.SYS20CON_SECUNTARIO || r.SYS02CON_SECUNTARIO || '';
        // Mejora #1: el boton Select (SYS16EVE=SELECT_GENERICO) no se muestra en el mapeo.
        this.toolbar = (r.Toolbar || []).filter((b: any) => b.Evento !== 'SELECT_GENERICO');
        this.cargandoMapa = false;

        if (!this.lista1.sys02con || !this.lista2.sys02con) {
          Swal.fire('Error', 'El backend no devolvio las dos listas (SYS02CON_PRIMARIO / SYS02CON_SECUNDARIO).', 'error');
          return;
        }

        // 2) y 3) Llena ambas listas con ListarGenerico
        this.cargarLista(this.lista1, 1);
        this.cargarLista(this.lista2, 1);
      },
      (err: any) => {
        this.cargandoMapa = false;
        Swal.fire('Error', err || 'No fue posible cargar la pantalla de mapeo', 'error');
      });
  }

  // Llama ListarGenerico para una lista y mapea la respuesta del advancetable.
  cargarLista(lista: ListaMapeo, pagina: number): void {
    lista.cargando = true;
    const body = {
      // Filtro unico (definido por lista1) aplicado por igual a ambas listas.
      FILTROS_OPCIONALES: [this.formatearFormulario({ ...this.filtrosAplicados })],
      FILTROS_OBLIGATORIOS: this.llavesPadre,   // llaves del padre => scope de la lista (vacio en top-level)
      PAGINA: pagina,
      OFFSET: lista.offset
    };
    this.dataService.ConsultarWS(this.urlListar + lista.sys02con, JSON.stringify(body)).subscribe(
      (resp: any) => {
        const estructura = resp.Estructura || [];
        lista.titulo = resp.SYS02DES || lista.titulo;
        lista.estructura = estructura;
        lista.columnas = estructura.filter((c: any) => c.A03VIS === 'S');
        lista.llaves = estructura.filter((c: any) => c.A03PKY === 'S').map((c: any) => c.A03COD);
        const filas = (resp.DataTable || []).map((row: any, i: number) => ({ ...row, INDEX: i, checkado: false }));
        lista.filas = filas;
        // Botonera de operaciones por fila (mejora #4): solo las que el backend marca visibles.
        lista.operaciones = (resp.Operaciones || []).filter((o: any) => o.SYS16MET === 'S');
        // Cargar los filtros SOLO la primera vez: reasignar en cada pagina reconstruiria el
        // sidebar y perderia lo que el usuario tiene escrito al paginar.
        if (!lista.filtros || lista.filtros.length === 0) {
          lista.filtros = resp.Filtros || [];
          if (lista === this.lista1) this.construirFiltros();   // el sidebar se define desde la lista1
        }
        lista.pagina = pagina;
        lista.hayMas = filas.length >= lista.offset;
        lista.todos = false;
        lista.cargando = false;
      },
      (err: any) => {
        lista.cargando = false;
        Swal.fire('Error', err || ('No fue posible cargar la lista ' + lista.sys02con), 'error');
      });
  }

  // ----- Filtro unico (barra sobre lista1) aplicado a AMBAS listas -----

  aplicarFiltros(objForm: any): void {
    this.filtrosAplicados = objForm || {};
    this.cargarLista(this.lista1, 1);
    this.cargarLista(this.lista2, 1);
  }

  // Deriva los campos del sidebar desde resp.Filtros de la lista1 ({FiltrosExpuestos, FiltrosAvanzado}).
  private construirFiltros(): void {
    const cont = (this.lista1.filtros && this.lista1.filtros[0]) || {};
    const exp: any[] = cont.FiltrosExpuestos || [];
    const avz: any[] = cont.FiltrosAvanzado || [];
    const mapear = (f: any, avanzado: boolean): CampoFiltro => ({
      label: f.label, title: f.title, type: f.type,
      placeholder: f.placeholder, options: f.options || [], avanzado
    });
    this.camposFiltro = [
      ...exp.filter(f => f.type !== 'hidden').map(f => mapear(f, false)),
      ...avz.filter(f => f.type !== 'hidden').map(f => mapear(f, true))
    ];
    this.filtroModel = {};
    this.camposFiltro.forEach(c => this.filtroModel[c.label] = c.type === 'select_multiple' ? [] : '');
  }

  // Boton Buscar del sidebar: aplica el modelo del filtro a ambas listas.
  buscar(): void {
    this.aplicarFiltros({ ...this.filtroModel });
  }

  limpiarFiltros(): void {
    this.camposFiltro.forEach(c => this.filtroModel[c.label] = c.type === 'select_multiple' ? [] : '');
    this.aplicarFiltros({});
  }

  // Espejo de advancedtable.formatearFormulario: normaliza nulos, arrays (IN), fechas y booleanos.
  // Los campos del filtro los define la lista1 => sus tipos se resuelven contra su estructura.
  private formatearFormulario(form: any): any {
    for (const key in form) {
      if (form[key] == null) {
        form[key] = '';
      }
      if (Array.isArray(form[key])) {
        form[key] = "'" + form[key].join("','") + "'";
      }
      if (form[key] === Number(form[key])) {
        form[key] = String(form[key]);
      }
      const elemento = this.getCampoEstructura(key);
      if (elemento && elemento.A03TIP === 'datetime' && form[key] !== '') {
        const [day, month, year] = String(form[key]).split('/');
        const dateValue = new Date(`${year}-${month}-${day}`);
        if (!isNaN(dateValue.getTime())) {
          form[key] = formatDate(`${year}-${month}-${day}`, 'dd/MM/yyyy', 'en');
        }
      }
    }
    return form;
  }

  private getCampoEstructura(label: string): any {
    return (this.lista1.estructura || []).find((c: any) => c.A03COD === label) || null;
  }

  // ----- Seleccion por checkbox -----

  marcarTodos(lista: ListaMapeo, checked: boolean): void {
    lista.todos = checked;
    lista.filas.forEach(f => f.checkado = checked);
  }

  onCheckFila(lista: ListaMapeo): void {
    lista.todos = lista.filas.length > 0 && lista.filas.every(f => f.checkado);
  }

  // Construye el arreglo de llaves de las filas marcadas de una lista.
  private seleccionDe(lista: ListaMapeo): any[] {
    return lista.filas
      .filter(f => f.checkado)
      .map(f => {
        const o: any = {};
        lista.llaves.forEach(k => o[k] = f[k]);
        return o;
      });
  }

  // ----- Toolbar (asociacion) -----

  ejecutarBoton(boton: any): void {
    const sel1 = this.seleccionDe(this.lista1);
    const sel2 = this.seleccionDe(this.lista2);
    const esMapeo = boton.Evento === 'MAPEO';   // SYS16EVE = MAPEO

    if (esMapeo) {
      // Mejora #2: el mapeo exige seleccion en AMBAS listas.
      if (sel1.length === 0 || sel2.length === 0) {
        Swal.fire('Atencion', 'Debe seleccionar al menos un registro en CADA lista para mapear.', 'warning');
        return;
      }
    } else if (sel1.length === 0 && sel2.length === 0) {
      Swal.fire('Atencion', 'Debe seleccionar al menos un registro en alguna de las listas.', 'warning');
      return;
    }

    const navId = (Date.now().toString(36) + Math.random().toString(36).substr(2, 5)).toUpperCase();
    const payload = {
      OPERACION: boton.Evento,
      LLAVES: sel1,                                                   // seleccion lista 1
      FILTROS_OBLIGATORIOS: sel2,                                     // seleccion lista 2
      LLAVES_LISTA1: sel1,                                            // (explicito) llaves marcadas lista 1
      LLAVES_LISTA2: sel2,                                            // (explicito) llaves marcadas lista 2
      LLAVES_PADRE: this.llavesPadre,                                 // llaves del registro padre (wizard); [] en top-level
      FILTROS_OPCIONALES: [this.formatearFormulario({ ...this.filtrosAplicados })],
      NAV_ID: navId
    };

    this.dataService.ConsultarWS(boton.URL, JSON.stringify(payload)).subscribe(
      (resp: any) => {
        Swal.fire({
          toast: true, position: 'bottom-end', icon: 'success',
          text: (boton.boton || 'Proceso') + ' ejecutado correctamente',
          showConfirmButton: false, timer: 3000
        });
        // EVENTO=MAPEO: refresca AMBAS listas por el metodo de busqueda (re-aplica el filtro actual
        // del sidebar); el resto de eventos hace una recarga simple.
        if (esMapeo) this.buscar();
        else this.recargarAmbas();
      },
      (err: any) => {
        Swal.fire({ title: 'Error', icon: 'error', html: '<div style="white-space:pre-line">' + (err || '') + '</div>' });
      });
  }

  private recargarAmbas(): void {
    this.cargarLista(this.lista1, 1);
    this.cargarLista(this.lista2, 1);
  }

  // ----- Operaciones por fila (botonera definida en resp.Operaciones de cada lista) -----

  // Llaves (PKs) de una fila concreta.
  private llavesDeFila(lista: ListaMapeo, fila: FilaMapeo): any {
    const o: any = {};
    lista.llaves.forEach(k => o[k] = fila[k]);
    return o;
  }

  // Click en un boton de operacion de una fila. Manda el mismo payload que advancedtable
  // ({ LLAVES:[llavesFila], URL, NAV_ID, OPERACION }) para reutilizar los WS existentes.
  ejecutarOperacion(lista: ListaMapeo, fila: FilaMapeo, op: any): void {
    if (!op || !op.URL) return;
    const navId = (Date.now().toString(36) + Math.random().toString(36).substr(2, 5)).toUpperCase();
    const payload = {
      LLAVES: [this.llavesDeFila(lista, fila)],
      URL: op.URL,
      NAV_ID: navId,
      OPERACION: op.Evento,
      LLAVES_PADRE: this.llavesPadre
    };

    const post = () => this.dataService.ConsultarWS(op.URL, JSON.stringify(payload)).subscribe(
      () => {
        Swal.fire({
          toast: true, position: 'bottom-end', icon: 'success',
          text: (op.boton || 'Operacion') + ' ejecutada correctamente',
          showConfirmButton: false, timer: 3000
        });
        this.cargarLista(lista, lista.pagina);   // recarga solo la lista afectada
      },
      (err: any) => Swal.fire({ title: 'Error', icon: 'error', html: '<div style="white-space:pre-line">' + (err || '') + '</div>' }));

    if (op.Evento === 'DELETE_WS' || op.Evento === 'DELETE_GENERICO' || op.Evento === 'ANULAR') {
      Swal.fire({
        title: op.Evento === 'ANULAR' ? 'Esta seguro de anular?' : 'Esta seguro de eliminar?',
        text: 'No podras revertir esto!', icon: 'warning',
        showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33',
        cancelButtonText: 'No, cancelar', confirmButtonText: op.Evento === 'ANULAR' ? 'Si, anular' : 'Si, eliminar'
      }).then(res => { if (res.isConfirmed) post(); });
    } else {
      post();
    }
  }

  // ----- Paginacion (servidor) -----

  paginaAnterior(lista: ListaMapeo): void {
    if (lista.pagina > 1) this.cargarLista(lista, lista.pagina - 1);
  }

  paginaSiguiente(lista: ListaMapeo): void {
    if (lista.hayMas) this.cargarLista(lista, lista.pagina + 1);
  }

  // Alineacion a la derecha para columnas numericas / monetarias.
  alineaDerecha(col: ColumnaMapeo): boolean {
    return col.SYS03TIP === 'F' || col.SYS03TIP === 'N';
  }
}
